import React, { useState, useMemo, useEffect } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const CHART_COLORS = {
  present: '#22c55e',
  absent: '#ef4444',
  late: '#f59e0b',
  excused: '#3b82f6',
  'early-departure': '#8b5cf6'
};

export default function AttendanceChart({ chartData, student, dateRange, selectedClass, onPointClick }) {
  const [chartType, setChartType] = useState('line');

  // Check if weekly view should be available (at least 15 records)
  const hasEnoughDataForWeekly = useMemo(() => {
    if (!chartData?.daily || !Array.isArray(chartData.daily)) return false;
    return chartData.daily.length >= 15;
  }, [chartData]);

  // Auto-switch from weekly to line if not enough data
  useEffect(() => {
    if (chartType === 'bar' && !hasEnoughDataForWeekly) {
      setChartType('line');
    }
  }, [chartType, hasEnoughDataForWeekly]);

  const chartOptions = useMemo(() => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 20,
          bottom: 20,
          left: 10,
          right: 10
        }
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 20,
            font: {
              size: 13,
              weight: '600'
            },
            color: '#334155'
          }
        },
        title: {
          display: true,
          text: selectedClass && selectedClass !== 'overview' 
            ? `${selectedClass} Attendance Chart - ${student?.first_name} ${student?.last_name}`
            : `Attendance Chart - ${student?.first_name} ${student?.last_name}`,
          font: {
            size: 16,
            weight: 'bold'
          },
          color: '#1e293b',
          padding: {
            bottom: 30
          }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#1e293b',
          bodyColor: '#475569',
          borderColor: '#e2e8f0',
          borderWidth: 1,
          cornerRadius: 12,
          padding: 12,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(tooltipItems) {
              const dataIndex = tooltipItems[0]?.dataIndex;
              
              if (chartType === 'bar' && dataIndex !== undefined && chartData?.daily) {
                // For weekly bar chart, we need to recalculate weekly data
                const weeklyData = {};
                const scoreValues = { present: 1, late: 0.5, absent: 0 }; // Excused excluded from scoring
                
                chartData.daily.forEach(day => {
                  // Parse date safely to avoid timezone shifts
                  let date;
                  if (typeof day.date === 'string' && day.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    const [year, month, dayNum] = day.date.split('-');
                    date = new Date(parseInt(year), parseInt(month) - 1, parseInt(dayNum));
                  } else {
                    date = new Date(day.date);
                  }
                  
                  const weekStart = new Date(date);
                  weekStart.setDate(date.getDate() - date.getDay());
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekStart.getDate() + 6);
                  const weekKey = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                  
                  if (!weeklyData[weekKey]) {
                    weeklyData[weekKey] = { totalScore: 0, maxScore: 0 };
                  }
                  
                  const dayScore = (day.present * scoreValues.present) + (day.late * scoreValues.late) + (day.absent * scoreValues.absent);
                  const scorableClasses = day.totalClasses - (day.excused || 0);
                  const dayMaxScore = scorableClasses * scoreValues.present;
                  weeklyData[weekKey].totalScore += dayScore;
                  weeklyData[weekKey].maxScore += dayMaxScore;
                });
                
                const weekKeys = Object.keys(weeklyData);
                if (weekKeys[dataIndex]) {
                  const weekData = weeklyData[weekKeys[dataIndex]];
                  return `${tooltipItems[0].label} - Score: ${weekData.totalScore}/${weekData.maxScore}`;
                }
              }
              
              if (dataIndex !== undefined && chartData?.daily?.[dataIndex]) {
                // Daily chart tooltip
                const dayData = chartData.daily[dataIndex];
                const scoreValues = { present: 1, late: 0.5, absent: 0 };
                const attendanceScore = (dayData.present * scoreValues.present) + 
                                       (dayData.late * scoreValues.late) + 
                                       (dayData.absent * scoreValues.absent);
                const scorableClasses = dayData.totalClasses - (dayData.excused || 0);
                const maxScore = scorableClasses * scoreValues.present;
                const scoreDisplay = `${attendanceScore}/${maxScore}`;
                
                return `${tooltipItems[0].label} - Score: ${scoreDisplay}`;
              }
              return tooltipItems[0]?.label || '';
            },
            label: function(context) {
              if (chartType === 'doughnut') return context.label + ': ' + context.parsed;
              
              const dataIndex = context.dataIndex;
              
              if (chartType === 'bar' && dataIndex !== undefined && chartData?.daily) {
                // Weekly bar chart tooltip - reconstruct weekly data to match chart
                const weeklyData = {};
                const scoreValues = { present: 1, late: 0.5, absent: 0 }; // Excused excluded from scoring
                
                chartData.daily.forEach(day => {
                  // Parse date safely to avoid timezone shifts
                  let date;
                  if (typeof day.date === 'string' && day.date.includes('-') && day.date.length === 10) {
                    // YYYY-MM-DD format - parse safely in local timezone
                    const [year, month, dayNum] = day.date.split('-').map(Number);
                    date = new Date(year, month - 1, dayNum);
                  } else {
                    date = new Date(day.date);
                  }
                  
                  const weekStart = new Date(date);
                  weekStart.setDate(date.getDate() - date.getDay()); // Get Sunday of that week
                  const weekKey = weekStart.toISOString().split('T')[0];
                  
                  if (!weeklyData[weekKey]) {
                    weeklyData[weekKey] = {
                      weekStart: weekStart,
                      actualDateRange: {
                        startDate: date,
                        endDate: date
                      },
                      totalScore: 0,
                      maxScore: 0,
                      present: 0,
                      late: 0,
                      absent: 0,
                      excused: 0,
                      totalClasses: 0
                    };
                  }
                  
                  // Track actual date range within the week
                  if (date < weeklyData[weekKey].actualDateRange.startDate) {
                    weeklyData[weekKey].actualDateRange.startDate = date;
                  }
                  if (date > weeklyData[weekKey].actualDateRange.endDate) {
                    weeklyData[weekKey].actualDateRange.endDate = date;
                  }
                  
                  weeklyData[weekKey].present += day.present;
                  weeklyData[weekKey].late += day.late;
                  weeklyData[weekKey].absent += day.absent;
                  weeklyData[weekKey].excused += day.excused || 0;
                  weeklyData[weekKey].totalClasses += day.totalClasses;
                  
                  const dayScore = (day.present * scoreValues.present) + (day.late * scoreValues.late) + (day.absent * scoreValues.absent);
                  const scorableClasses = day.totalClasses - (day.excused || 0);
                  const dayMaxScore = scorableClasses * scoreValues.present;
                  weeklyData[weekKey].totalScore += dayScore;
                  weeklyData[weekKey].maxScore += dayMaxScore;
                });
                
                const sortedWeeks = Object.values(weeklyData).sort((a, b) => a.weekStart - b.weekStart);
                if (sortedWeeks[dataIndex]) {
                  const weekData = sortedWeeks[dataIndex];
                  const scoreRate = weekData.maxScore > 0 ? (weekData.totalScore / weekData.maxScore) * 100 : 0;
                  
                  // Create accurate date range label
                  const startDate = weekData.actualDateRange.startDate;
                  const endDate = weekData.actualDateRange.endDate;
                  
                  let periodLabel;
                  if (startDate.toDateString() === endDate.toDateString()) {
                    periodLabel = startDate.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  } else {
                    periodLabel = `${startDate.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })} - ${endDate.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}`;
                  }
                  
                  return [
                    `Period: ${periodLabel}`,
                    `Weekly Score: ${weekData.totalScore}/${weekData.maxScore}`,
                    `Present: ${weekData.present} classes`,
                    `Late: ${weekData.late} classes`,
                    `Absent: ${weekData.absent} classes`,
                    `Excused: ${weekData.excused} classes`,
                    `Total: ${weekData.totalClasses} classes`,
                    `Attendance Rate: ${scoreRate.toFixed(1)}%`
                  ];
                }
              }
              
              if (dataIndex !== undefined && chartData?.daily?.[dataIndex]) {
                // Daily line chart tooltip
                const dayData = chartData.daily[dataIndex];
                const scoreValues = { present: 1, late: 0.5, absent: 0 }; // Excused excluded from scoring
                const attendanceScore = (dayData.present * scoreValues.present) + 
                                       (dayData.late * scoreValues.late) + 
                                       (dayData.absent * scoreValues.absent);
                const scorableClasses = dayData.totalClasses - (dayData.excused || 0);
                const maxScore = scorableClasses * scoreValues.present;
                
                return [
                  `Attendance Score: ${attendanceScore}/${maxScore}`,
                  `Present: ${dayData.present} classes`,
                  `Late: ${dayData.late} classes`,
                  `Absent: ${dayData.absent} classes`,
                  `Excused: ${dayData.excused || 0} classes`,
                  `Total: ${dayData.totalClasses} classes`
                ];
              }
              return `Attendance: ${context.parsed.y}`;
            },
            afterBody: function(tooltipItems) {
              return []; // Remove the old afterBody content since we moved it to label
            }
          }
        }
      },
      scales: chartType === 'doughnut' ? {} : {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(148, 163, 184, 0.1)',
            borderColor: 'rgba(148, 163, 184, 0.2)'
          },
          ticks: {
            color: '#64748b',
            font: {
              size: 12,
              weight: '500'
            },
            padding: 8,
            callback: function(value) {
              if (chartType === 'line') {
                return value.toFixed(1); // Show score with 1 decimal
              }
              return value;
            }
          },
          title: {
            display: true,
            text: chartType === 'line' ? 'Attendance Score' : 'Count',
            color: '#475569',
            font: {
              size: 13,
              weight: '600'
            },
            padding: 12
          }
        },
        x: {
          display: chartType !== 'doughnut',
          grid: {
            color: 'rgba(148, 163, 184, 0.1)',
            borderColor: 'rgba(148, 163, 184, 0.2)'
          },
          ticks: {
            color: '#64748b',
            font: {
              size: 12,
              weight: '500'
            },
            padding: 8,
            maxRotation: 45,
            maxTicksLimit: 10
          },
          title: {
            display: true,
            text: 'Date',
            color: '#475569',
            font: {
              size: 13,
              weight: '600'
            },
            padding: 12
          }
        }
      },
      onClick: (event, activeElements) => {
        // Only handle clicks for line and bar charts (not doughnut)
        if ((chartType === 'line' || chartType === 'bar') && activeElements && activeElements.length > 0 && onPointClick) {
          const dataIndex = activeElements[0].index;
          
          if (chartType === 'line' && chartData?.daily?.[dataIndex]) {
            // For line chart, get the exact date
            const selectedDate = chartData.daily[dataIndex].date;
            onPointClick({ type: 'day', date: selectedDate });
          } else if (chartType === 'bar' && chartData?.daily) {
            // For bar chart, reconstruct the weekly grouping to match the processed data
            const weeklyData = {};
            
            chartData.daily.forEach(day => {
              // Parse date safely to avoid timezone shifts
              let date;
              if (typeof day.date === 'string' && day.date.includes('-') && day.date.length === 10) {
                // YYYY-MM-DD format - parse safely in local timezone
                const [year, month, dayNum] = day.date.split('-').map(Number);
                date = new Date(year, month - 1, dayNum);
              } else {
                date = new Date(day.date);
              }
              
              const weekStart = new Date(date);
              weekStart.setDate(date.getDate() - date.getDay()); // Get Sunday of that week
              const weekKey = weekStart.toISOString().split('T')[0];
              
              if (!weeklyData[weekKey]) {
                weeklyData[weekKey] = {
                  weekStart: weekStart,
                  actualDateRange: {
                    startDate: date,
                    endDate: date
                  },
                  dates: []
                };
              }
              
              // Track actual date range within the week
              if (date < weeklyData[weekKey].actualDateRange.startDate) {
                weeklyData[weekKey].actualDateRange.startDate = date;
              }
              if (date > weeklyData[weekKey].actualDateRange.endDate) {
                weeklyData[weekKey].actualDateRange.endDate = date;
              }
              
              weeklyData[weekKey].dates.push(day.date);
            });
            
            const sortedWeeks = Object.values(weeklyData).sort((a, b) => a.weekStart - b.weekStart);
            if (sortedWeeks[dataIndex]) {
              const weekInfo = sortedWeeks[dataIndex];
              onPointClick({ 
                type: 'week', 
                startDate: weekInfo.actualDateRange.startDate.toISOString().split('T')[0],
                endDate: weekInfo.actualDateRange.endDate.toISOString().split('T')[0],
                dates: weekInfo.dates
              });
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };

    return baseOptions;
  }, [chartType, student, chartData, selectedClass, onPointClick]);

  const prepareLineChartData = () => {
    if (!chartData?.daily) return null;

    // Define scoring system - excused excluded from scoring
    const scoreValues = {
      present: 1,
      late: 0.5,
      absent: 0
    };

    // Calculate scores for each day
    const processedData = chartData.daily.map(day => {
      const attendanceScore = (day.present * scoreValues.present) + 
                             (day.late * scoreValues.late) + 
                             (day.absent * scoreValues.absent);
      // Exclude excused classes from max possible score calculation
      const scorableClasses = day.totalClasses - (day.excused || 0);
      const maxPossibleScore = scorableClasses * scoreValues.present;
      
      return {
        ...day,
        attendanceScore,
        maxScore: maxPossibleScore,
        scoreDisplay: `${attendanceScore}/${maxPossibleScore}`,
        scoreRate: maxPossibleScore > 0 ? (attendanceScore / maxPossibleScore) * 100 : 0
      };
    });

    return {
      labels: processedData.map(item => {
        // Parse the date string safely to avoid timezone shifts
        // Since backend now returns YYYY-MM-DD format in school timezone
        if (typeof item.date === 'string' && item.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = item.date.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          });
        } else {
          // Fallback for other date formats
          const date = new Date(item.date);
          return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          });
        }
      }),
      datasets: [{
        label: 'Daily Attendance Score',
        data: processedData.map(day => day.attendanceScore),
        borderColor: CHART_COLORS.present,
        backgroundColor: `${CHART_COLORS.present}20`,
        borderWidth: 3,
        pointBackgroundColor: processedData.map(day => {
          if (day.primaryStatus === 'present') return CHART_COLORS.present;
          if (day.primaryStatus === 'late') return CHART_COLORS.late;
          return CHART_COLORS.absent;
        }),
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        fill: false
      }],
      processedData // Include processed data for tooltip access
    };
  };

  const prepareBarChartData = () => {
    if (!chartData?.daily) return null;

    // Define scoring system - excused excluded from scoring
    const scoreValues = {
      present: 1,
      late: 0.5,
      absent: 0
    };

    // Group daily data by week and track actual date ranges
    const weeklyData = {};
    
    chartData.daily.forEach(day => {
      // Parse date safely to avoid timezone shifts
      let date;
      if (typeof day.date === 'string' && day.date.includes('-') && day.date.length === 10) {
        // YYYY-MM-DD format - parse safely in local timezone
        const [year, month, dayNum] = day.date.split('-').map(Number);
        date = new Date(year, month - 1, dayNum);
      } else {
        date = new Date(day.date);
      }
      
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Get Sunday of that week
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Get Saturday of that week
      
      // Use week start as key for grouping
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {
          weekStart: weekStart,
          actualDateRange: {
            startDate: date,
            endDate: date
          },
          totalScore: 0,
          maxScore: 0,
          present: 0,
          late: 0,
          absent: 0,
          excused: 0,
          totalClasses: 0,
          dates: []
        };
      }
      
      // Track actual date range within the week
      if (date < weeklyData[weekKey].actualDateRange.startDate) {
        weeklyData[weekKey].actualDateRange.startDate = date;
      }
      if (date > weeklyData[weekKey].actualDateRange.endDate) {
        weeklyData[weekKey].actualDateRange.endDate = date;
      }
      
      // Store all dates for this week
      weeklyData[weekKey].dates.push(date);
      
      // Accumulate weekly totals
      weeklyData[weekKey].present += day.present;
      weeklyData[weekKey].late += day.late;
      weeklyData[weekKey].absent += day.absent;
      weeklyData[weekKey].excused += day.excused || 0;
      weeklyData[weekKey].totalClasses += day.totalClasses;
      
      // Calculate weekly score
      const dayScore = (day.present * scoreValues.present) + 
                      (day.late * scoreValues.late) + 
                      (day.absent * scoreValues.absent);
      // Exclude excused classes from max possible score calculation
      const scorableClasses = day.totalClasses - (day.excused || 0);
      const dayMaxScore = scorableClasses * scoreValues.present;
      
      weeklyData[weekKey].totalScore += dayScore;
      weeklyData[weekKey].maxScore += dayMaxScore;
    });

    const processedWeeks = Object.values(weeklyData).map(week => {
      // Create accurate date range label
      const startDate = week.actualDateRange.startDate;
      const endDate = week.actualDateRange.endDate;
      
      // If start and end are the same, show single date
      let weekLabel;
      if (startDate.toDateString() === endDate.toDateString()) {
        weekLabel = startDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      } else {
        weekLabel = `${startDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })} - ${endDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })}`;
      }
      
      return {
        ...week,
        weekLabel,
        scoreDisplay: `${week.totalScore}/${week.maxScore}`,
        scoreRate: week.maxScore > 0 ? (week.totalScore / week.maxScore) * 100 : 0
      };
    }).sort((a, b) => a.weekStart - b.weekStart); // Sort by week start date

    return {
      labels: processedWeeks.map(week => week.weekLabel),
      datasets: [
        {
          label: 'Weekly Attendance Score',
          data: processedWeeks.map(week => week.totalScore),
          backgroundColor: processedWeeks.map(week => {
            if (week.scoreRate >= 95) return 'rgba(34, 197, 94, 0.8)'; // Green
            if (week.scoreRate >= 80) return 'rgba(245, 158, 11, 0.8)'; // Yellow
            if (week.scoreRate >= 60) return 'rgba(249, 115, 22, 0.8)'; // Orange
            return 'rgba(239, 68, 68, 0.8)'; // Red
          }),
          borderColor: processedWeeks.map(week => {
            if (week.scoreRate >= 95) return '#16a34a';
            if (week.scoreRate >= 80) return '#d97706';
            if (week.scoreRate >= 60) return '#ea580c';
            return '#dc2626';
          }),
          borderWidth: 2
        }
      ],
      processedWeeks // Include for tooltip access
    };
  };

  const prepareDoughnutData = () => {
    if (!chartData?.daily) return null;

    // Calculate status counts from daily data
    const statusCounts = {
      present: 0,
      absent: 0,
      late: 0,
      excused: 0
    };

    // Sum up all the daily counts
    chartData.daily.forEach(day => {
      statusCounts.present += day.present || 0;
      statusCounts.absent += day.absent || 0;
      statusCounts.late += day.late || 0;
      statusCounts.excused += day.excused || 0;
    });

    // Filter out statuses with zero counts for cleaner display
    const filteredStatusCounts = {};
    Object.keys(statusCounts).forEach(status => {
      if (statusCounts[status] > 0) {
        filteredStatusCounts[status] = statusCounts[status];
      }
    });

    const labels = Object.keys(filteredStatusCounts);
    const data = Object.values(filteredStatusCounts);
    const colors = labels.map(status => CHART_COLORS[status] || '#6b7280');

    return {
      labels: labels.map(label => label.charAt(0).toUpperCase() + label.slice(1)),
      datasets: [
        {
          data: data,
          backgroundColor: colors,
          borderColor: colors.map(color => color),
          borderWidth: 2,
          hoverOffset: 4
        }
      ]
    };
  };

  const getChartData = () => {
    switch (chartType) {
      case 'line':
        return prepareLineChartData();
      case 'bar':
        return prepareBarChartData();
      case 'doughnut':
        return prepareDoughnutData();
      default:
        return null;
    }
  };

  const renderChart = () => {
    const data = getChartData();
    if (!data) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>No chart data available</p>
          </div>
        </div>
      );
    }

    const chartProps = {
      data,
      options: chartOptions
    };

    switch (chartType) {
      case 'line':
        return <Line {...chartProps} />;
      case 'bar':
        return <Bar {...chartProps} />;
      case 'doughnut':
        return <Doughnut {...chartProps} />;
      default:
        return null;
    }
  };

  if (!chartData || !student) {
    return null;
  }

  return (
    <div className="relative">
      {/* Modern Chart Type Selector */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
          <h3 className="text-xl font-bold text-slate-900">Period Attendance Visualization</h3>
        </div>
        <div className="flex space-x-1 bg-slate-100 rounded-xl p-1">
          {[
            { type: 'line', label: 'Timeline', icon: '📈' },
            ...(hasEnoughDataForWeekly ? [{ type: 'bar', label: 'Weekly', icon: '📊' }] : []),
            { type: 'doughnut', label: 'Summary', icon: '🥧' }
          ].map(({ type, label, icon }) => (
            <button
              key={type}
              onClick={() => setChartType(type)}
              className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
                chartType === type
                  ? 'bg-white text-blue-600 shadow-lg shadow-blue-100 transform scale-105'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-white/50'
              }`}
            >
              <span className="mr-2">{icon}</span>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Modern Chart Container with increased height */}
      <div className="h-[38.25rem] relative bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200/60 shadow-xl shadow-slate-200/50 p-6">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 rounded-2xl"></div>
        
        {/* Inline Legend/Key positioned in bottom-left of chart - horizontal layout */}
        <div className="absolute bottom-4 left-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-slate-200/50">
          {chartType === 'line' && (
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500 mr-1.5 shadow-sm"></div>
                <span className="font-medium text-green-800">Present (1)</span>
              </div>
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 mr-1.5 shadow-sm"></div>
                <span className="font-medium text-yellow-800">Late (0.5)</span>
              </div>
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500 mr-1.5 shadow-sm"></div>
                <span className="font-medium text-red-800">Absent (0)</span>
              </div>
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mr-1.5 shadow-sm"></div>
                <span className="font-medium text-blue-800">Excused (N/A)</span>
              </div>
            </div>
          )}
          {chartType === 'bar' && (
            <div className="text-xs text-slate-700 font-medium">
              📊 Weekly breakdown
            </div>
          )}
          {chartType === 'doughnut' && (
            <div className="text-xs text-slate-700 font-medium">
              🥧 Overall distribution
            </div>
          )}
        </div>
        
        <div className="relative h-full">
          {renderChart()}
        </div>
      </div>
    </div>
  );
}